/**
 * Lightweight wrapper around Google Ads / Analytics gtag.
 *
 * The base gtag.js snippet is loaded once in `public/index.html`. This helper
 * exposes a single `track(eventName, params)` function that:
 *  - silently no-ops if gtag is not loaded (dev, ad-blockers, SSR)
 *  - never throws (analytics must never break the UI)
 *  - dedupes events that should only fire once per session via the `dedupeKey`
 *    option (e.g. one purchase event per Stripe session_id).
 */

const SS_PREFIX = "gtag_fired:";

export function track(eventName, params = {}, options = {}) {
  try {
    const { dedupeKey } = options;
    if (dedupeKey) {
      try {
        const k = SS_PREFIX + dedupeKey;
        if (sessionStorage.getItem(k)) return false;
        sessionStorage.setItem(k, "1");
      } catch {
        /* sessionStorage unavailable (Safari private mode) — fire anyway */
      }
    }
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return false;
    }
    window.gtag("event", eventName, params);
    return true;
  } catch {
    return false;
  }
}

// Pre-canned conversion helpers --------------------------------------------

/** Fired immediately after a successful POST /api/auth/register. */
export function trackSignUp(method = "email") {
  return track("sign_up", { method });
}

/**
 * Fired when the user clicks "Subscribe" / "Prova 14 giorni" and we redirect
 * to Stripe Checkout. value is in EUR (cents → EUR before passing).
 */
export function trackBeginCheckout({ value, currency = "EUR", plan }) {
  return track("begin_checkout", {
    value: typeof value === "number" ? value : undefined,
    currency,
    items: plan
      ? [
          {
            item_id: plan,
            item_name: plan,
            price: typeof value === "number" ? value : undefined,
            quantity: 1,
          },
        ]
      : undefined,
  });
}

/**
 * Fired once per Stripe session_id when /api/payments/status returns paid.
 * value is in EUR.
 */
export function trackPurchase({ sessionId, value, currency = "EUR", kind }) {
  if (!sessionId) return false;
  return track(
    "purchase",
    {
      transaction_id: sessionId,
      value: typeof value === "number" ? value : undefined,
      currency,
      items: kind
        ? [
            {
              item_id: kind,
              item_name: kind,
              price: typeof value === "number" ? value : undefined,
              quantity: 1,
            },
          ]
        : undefined,
    },
    { dedupeKey: `purchase:${sessionId}` },
  );
}
